import type { ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useDraggable } from './useDraggable'

export interface FormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  /** The form fields. The dialog owns the <form>, so children are inputs only. */
  children: ReactNode
  submitLabel?: string
  cancelLabel?: string
  /** Disables the submit button and shows the pending label. */
  loading?: boolean
  /** Server-side failure, shown above the buttons rather than swallowed. */
  error?: string | null
  onSubmit: () => void
  size?: 'md' | 'lg'
}

/**
 * A modal that holds a form.
 *
 * <p>Distinct from `ConfirmDialog`, which asks a yes/no question. This one owns the `<form>`
 * element itself so Enter submits and the browser's own required-field handling works —
 * a dialog whose "Save" is an onClick handler silently breaks both, and keyboard users feel
 * it first.
 *
 * <p>Errors render inside the dialog. A form that closes on failure and shows a toast
 * somewhere else has thrown away everything the person typed.
 *
 * <p>The dialog can be dragged by its title bar. It is a modal, so whatever the person
 * needs to read — the row they are editing, a value further down the table — is behind an
 * overlay they cannot move, and the only way to see it was to cancel and lose their typing.
 * Moving the dialog is cheaper than that. Radix's own `onPointerDownOutside` is untouched,
 * so clicking away still behaves as before.
 */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  loading,
  error,
  onSubmit,
  size = 'md',
}: FormDialogProps) {
  const { panelRef, offset, dragging, handleProps, reset } = useDraggable()

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // Put it back in the middle on the way out, so the next open is centred.
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          ref={panelRef}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 max-h-[90vh] overflow-y-auto overscroll-contain',
            'rounded-[var(--radius-card)] bg-[var(--color-card)] p-6 shadow-[var(--shadow-popover)]',
            size === 'lg' ? 'w-[min(48rem,94vw)]' : 'w-[min(32rem,94vw)]',
            dragging && 'select-none',
          )}
          // The centring translate stays in the transform and the drag is added to it, so the
          // dialog opens centred without being measured and the offset means "how far the
          // person moved it" rather than an absolute position.
          style={{
            transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
          }}
        >
          <div
            {...handleProps}
            className={cn(
              'mb-4 flex items-start justify-between gap-4',
              dragging ? 'cursor-grabbing' : 'cursor-grab',
            )}
          >
            <div className="min-w-0">
              <Dialog.Title className="text-base font-semibold text-[var(--color-heading)]">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-sm text-[var(--color-muted)]">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="icon" aria-label="Close">
                <X className="size-4" aria-hidden="true" />
              </Button>
            </Dialog.Close>
          </div>

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault()
              onSubmit()
            }}
          >
            {children}

            {error && (
              <p role="alert" className="text-sm text-[var(--color-danger)]">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Dialog.Close asChild>
                <Button type="button" variant="ghost">
                  {cancelLabel}
                </Button>
              </Dialog.Close>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving…' : submitLabel}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
